export type PurlParameterMapArray = Record<string, string | string[]>;
export type PurlParameterMap = Record<string, string>;

export interface PurlAttrMap {
    source: string;
    protocol: string;
    user: string;
    password: string;
    host: string;
    port: string;
    relative: string;
    path: string;
    directory: string;
    file: string;
    query: string;
    fragment: string;
    base: string;
    anchor: string;
}

function setParamValue(target: PurlParameterMapArray, key: string, value: string): void {
    const prev = target[key];
    if(typeof prev === 'undefined') {
        target[key] = value;
        return;
    }
    if(Array.isArray(prev)) {
        prev.push(value);
        return;
    }
    target[key] = [prev, value];
}

function parseParams(input: string): PurlParameterMapArray {
    const map: PurlParameterMapArray = {};
    const params = new URLSearchParams(input);
    params.forEach((value, key) => {
        setParamValue(map, key, value);
    });
    return map;
}

function splitSegments(pathValue: string): string[] {
    const trimmed = pathValue.replace(/^\/+|\/+$/g, '');
    if(!trimmed) {
        return [];
    }
    return trimmed.split('/');
}

function buildAttrMap(source: string, parsedUrl: URL): PurlAttrMap {
    const protocol = parsedUrl.protocol.replace(/:$/, '');
    const user = parsedUrl.username ? decodeURIComponent(parsedUrl.username) : '';
    const password = parsedUrl.password ? decodeURIComponent(parsedUrl.password) : '';
    const path = parsedUrl.pathname || '';
    const pathSegments = splitSegments(path);
    const file = pathSegments.length ? pathSegments[pathSegments.length - 1] ?? '' : '';
    const hasTrailingSlash = path.endsWith('/');
    const directory = file === ''
        ? path
        : path.slice(0, Math.max(0, path.length - file.length)) || (hasTrailingSlash ? path : '');
    const query = parsedUrl.search.replace(/^\?/, '');
    const fragment = parsedUrl.hash.replace(/^#/, '');

    return {
        source,
        protocol,
        user,
        password,
        host: parsedUrl.hostname,
        port: parsedUrl.port,
        relative: `${path}${parsedUrl.search}${parsedUrl.hash}`,
        path,
        directory,
        file,
        query,
        fragment,
        base: parsedUrl.host ? `${protocol ? `${protocol}://` : ''}${parsedUrl.host}` : '',
        anchor: fragment,
    };
}

function parseWithUrl(input?: string): URL {
    if(typeof window !== 'undefined' && window.location) {
        const base = window.location.href;
        const source = input ?? base;
        return new URL(source, base);
    }

    const fallbackBase = 'http://localhost/';
    return new URL(input ?? fallbackBase, fallbackBase);
}

export function purl(input?: string) {
    const someUrl = input;
    const source = someUrl ?? (typeof window !== 'undefined' && window.location ? window.location.href : '');
    const parsed = parseWithUrl(someUrl);

    const queryAttr = buildAttrMap(source, parsed);
    const queryParamsArray = parseParams(queryAttr.query);
    const queryParams = Object.fromEntries(
        Object.entries(queryParamsArray).map(([key, value]) => [key, typeof value === 'string' ? value : value[0]]),
    ) as PurlParameterMap;

    function attr(): PurlAttrMap;
    function attr(option: keyof PurlAttrMap | 'anchor'): string;
    function attr(option?: keyof PurlAttrMap | 'anchor'): string | PurlAttrMap {
        if(option === undefined) {
            return queryAttr;
        }
        const key = option === 'anchor' ? 'fragment' : option;
        return queryAttr[key] ?? '';
    }

    function exparam(): PurlParameterMapArray;
    function exparam(parameterName: string): string | string[] | undefined;
    function exparam(parameterName?: string): string | string[] | undefined | PurlParameterMapArray {
        if(parameterName === undefined) {
            return queryParamsArray;
        }
        return queryParamsArray[parameterName];
    }

    function param(): PurlParameterMap;
    function param(parameterName: string): string | undefined;
    function param(parameterName?: string): string | undefined | PurlParameterMap {
        if(parameterName === undefined) {
            return queryParams;
        }
        return queryParams[parameterName];
    }

    return {
        data: {
            ...queryAttr,
            param: queryParamsArray,
        },
        attr,
        exparam,
        param,
    };
}

export type PurlUrl = ReturnType<typeof purl>;
